/**
 * A critically damped spring (damping ratio 1, no overshoot) in Apple's
 * terms: `response` is the period in seconds of the undamped spring, not a
 * duration. Integrated with its velocity, so a new target mid-flight carries
 * the current velocity on and the motion reverses without a jump.
 */

export interface SpringState {
  value: number;
  velocity: number;
}

/** The default response for a move that no gesture threw (seconds). */
export const SPRING_RESPONSE = 0.38;

/** Integration step: fine enough that any frame rate settles alike. */
const MAX_STEP = 1 / 240;
/** Value and velocity this close to rest count as settled. */
const SETTLE_DISTANCE = 0.001;
const SETTLE_VELOCITY = 0.01;

/** Advances `state` by `dt` seconds toward `target`. */
export function stepSpring(
  state: SpringState,
  target: number,
  dt: number,
  response = SPRING_RESPONSE,
): SpringState {
  const omega = (2 * Math.PI) / response;
  let { value, velocity } = state;
  let left = Math.max(0, dt);
  while (left > 0) {
    const step = Math.min(MAX_STEP, left);
    const acceleration =
      -omega * omega * (value - target) - 2 * omega * velocity;
    velocity += acceleration * step;
    value += velocity * step;
    left -= step;
  }
  return { value, velocity };
}

/** Whether the spring has come to rest at `target`. */
export function springSettled(state: SpringState, target: number): boolean {
  return (
    Math.abs(state.value - target) < SETTLE_DISTANCE &&
    Math.abs(state.velocity) < SETTLE_VELOCITY
  );
}
